import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

export default class VariantPicker extends Component {
  /** @type {Swiper|null} */
  thumbsSwiper = null;
  /** @type {Swiper|null} */
  mainSwiper = null;
  /** @type {number|undefined} */
  galleryInitTimer;
  #pendingRequestUrl;
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged);
    this.initGallery();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.variantChanged);
    window.clearTimeout(this.galleryInitTimer);
    this.destroyGallery();
  }

  get gallery() {
    const productGrid = this.closest('[data-product-grid-content]');

    if (!productGrid || this.closest('product-card')) return null;

    return productGrid.querySelector('.custom-gallery');
  }

  destroyGallery() {
    this.mainSwiper?.destroy(true, true);
    this.thumbsSwiper?.destroy(true, true);
    this.mainSwiper = null;
    this.thumbsSwiper = null;
  }

  initGallery(attempt = 0) {
    window.clearTimeout(this.galleryInitTimer);

    const gallery = this.gallery;
    if (!gallery) return;

    if (!window.Swiper) {
      if (attempt < 100) {
        this.galleryInitTimer = window.setTimeout(() => {
          if (this.isConnected) this.initGallery(attempt + 1);
        }, 50);
      }
      return;
    }

    const mainEl = gallery.querySelector('.custom-gallery__main.swiper');
    const thumbsEl = gallery.querySelector('.custom-gallery__thumbs.swiper');
    if (!mainEl || !thumbsEl) return;

    const hasMultipleSlides = mainEl.querySelectorAll('.swiper-slide').length > 1;

    this.destroyGallery();

    this.thumbsSwiper = new window.Swiper(thumbsEl, {
      spaceBetween: 10,
      slidesPerView: 4,
      observer: true,
      observeParents: true,
      watchSlidesProgress: true,
    });

    this.mainSwiper = new window.Swiper(mainEl, {
      spaceBetween: 10,
      loop: hasMultipleSlides,
      observer: true,
      observeParents: true,
      pagination: {
        el: gallery.querySelector('.custom-gallery__pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: gallery.querySelector('.custom-gallery__next'),
        prevEl: gallery.querySelector('.custom-gallery__prev'),
      },
      thumbs: {
        swiper: this.thumbsSwiper,
      },
      initialSlide: 0,
    });

    this.mainSwiper.slideToLoop(0, 0);
    this.thumbsSwiper.slideTo(0, 0);
  }

  variantChanged = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: event.target.dataset.optionValueId ?? ''
    }));

    if (this.mainSwiper && this.thumbsSwiper) {
      this.mainSwiper.slideToLoop(0);
      this.thumbsSwiper.slideTo(0);
    }

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);
    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }
    if (loadsNewProduct) url.pathname = newUrl;
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  };

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const el = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!el) throw new Error('Target element not found');
      target = el;
    }
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newOption = Array.from(target.options).find(o => o.value === newValue);
      if (!newOption) throw new Error('Option not found');
      target.querySelectorAll('option').forEach(o => o.removeAttribute('selected'));
      newOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceValues = []) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') ||
        this.closest('swatches-variant-picker-component')) {
      if (productUrl.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then(r => r.text())
      .then(text => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(text, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const jsonContent =
          html.querySelector(`variant-picker script[type="application/json"]`)
              ?.textContent;
        if (!jsonContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          if (this.selectedOptionId) {
            this.dispatchEvent(new VariantUpdateEvent(
              JSON.parse(jsonContent),
              this.selectedOptionId,
              { html, productId: this.dataset.productId ?? '', newProduct }
            ));
          }

          this.updateGallery(html);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') console.error(err);
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const newPicker = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newPicker) throw new Error('No new variant picker source found');

    if (newPicker instanceof HTMLElement) {
      const id = newPicker.dataset.productId;
      const url = newPicker.dataset.productUrl;
      if (id && url && this.dataset.productId !== id) {
        newProduct = { id, url };
      }
      this.dataset.productId = id;
      this.dataset.productUrl = url;
    }

    morph(this, newPicker);
    return newProduct;
  }

  updateGallery(newHtml) {
    const newGallery = newHtml.querySelector('[data-product-grid-content] .custom-gallery');
    const currentGallery = this.gallery;
    if (!newGallery || !currentGallery) return;

    this.destroyGallery();
    morph(currentGallery, newGallery);
    this.initGallery();
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);

    setTimeout(() => {
      const picker = document.querySelector('[data-product-grid-content] variant-picker');
      if (picker && typeof picker.initGallery === 'function') {
        picker.initGallery();
      }
    }, 0);
  }

  get selectedOption() {
    const opt = this.querySelector('select option[selected], fieldset input:checked');
    return (opt instanceof HTMLInputElement || opt instanceof HTMLOptionElement)
      ? opt
      : undefined;
  }

  get selectedOptionId() {
    const sel = this.selectedOption;
    if (!sel) return undefined;
    const id = sel.dataset.optionValueId;
    if (!id) throw new Error('No option value ID found');
    return id;
  }

  get selectedOptionsValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(opt => {
      const id = opt.dataset.optionValueId;
      if (!id) throw new Error('No option value ID found');
      return id;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
